import {
  AlipayCircleFilled,
  CheckCircleOutlined,
  CheckCircleTwoTone,
  LoadingOutlined,
  PayCircleFilled,
  RollbackOutlined,
  SendOutlined,
  WechatFilled,
} from '@ant-design/icons';
import { Space, Tag, Typography } from 'antd';
import React from 'react';
import { IconDY, IconMT } from '@/components/Icons';
import {
  Order,
  OrderChannel,
  OrderStatusMap,
  OrderType,
  orderTypeDescMap,
} from '@/models/order';
import { PaymentChannel } from '@/models/payment';
import { BalanceLog } from '@/models/poiConfig';
import { assertUnreachable } from '@/utils/common';

export const getOrderChannelDesc = (order: Order) => {
  switch (order.channel) {
    case OrderChannel.Store:
      return <Tag color={'blue'}>门店</Tag>;
    case OrderChannel.Weapp:
      return <Tag color={'cyan'}>小程序</Tag>;
    case undefined:
      return null;
  }
  return assertUnreachable(order.channel);
};

export const getOrderTypeDesc = (order: Order) => {
  const text = orderTypeDescMap[order.orderType];
  if (order.orderType === OrderType.Pay) {
    return <Tag color={'purple'}>{text}</Tag>;
  } else if (order.orderType === OrderType.SecKill) {
    return <Tag color={'magenta'}>{text}</Tag>;
  } else if (order.orderType === OrderType.Normal) {
    return <Tag color={'green'}>{text}</Tag>;
  } else if (order.orderType === OrderType.Recharge) {
    return <Tag color={'red'}>{text}</Tag>;
  } else if (order.orderType === OrderType.Card) {
    return <Tag color={'orange'}>{text}</Tag>;
  } else if (order.orderType === OrderType.GROUPON_MT) {
    return <Tag color={'#fad105'}>{text}</Tag>;
  } else if (order.orderType === OrderType.GROUPON_DY) {
    return <Tag color={'#111111'}>{text}</Tag>;
  }
  return null;
};

export const getBalanceLogTypeDesc = (log: BalanceLog) => {
  return <Tag color={log.value > 0 ? 'green' : 'red'}>{log.typeDesc}</Tag>;
};

export const getOrderStatusDesc = (order: Order) => {
  if (order.orderType === OrderType.Pay) {
    if (order.orderStatus === OrderStatusMap.Complete.type) {
      return (
        <Tag color={'#87d068'}>
          <CheckCircleOutlined /> 已支付
        </Tag>
      );
    }
    return <Tag>未支付</Tag>;
  }

  switch (order.orderStatus) {
    case OrderStatusMap.UnPaid.type:
      return <Tag color={'blue'}>{OrderStatusMap.UnPaid.desc}</Tag>;
    case OrderStatusMap.UserCanceled.type:
      return <Typography.Text disabled>{OrderStatusMap.UserCanceled.desc}</Typography.Text>;
    case OrderStatusMap.SystemCanceled.type:
      return <Typography.Text disabled>{OrderStatusMap.SystemCanceled.desc}</Typography.Text>;
    case OrderStatusMap.Paid.type:
      return <Tag color={'#fa8c16'}>{OrderStatusMap.Paid.desc}</Tag>;
    case OrderStatusMap.Shipped.type:
      return (
        <Tag color={'#68d0d0'}>
          <SendOutlined /> {OrderStatusMap.Shipped.desc}
        </Tag>
      );
    case OrderStatusMap.Refunding.type:
      return (
        <Tag color={'orange'}>
          <RollbackOutlined /> {OrderStatusMap.Refunding.desc}
        </Tag>
      );
    case OrderStatusMap.Refunded.type:
      return (
        <Tag color={'volcano'}>
          <RollbackOutlined /> {OrderStatusMap.Refunded.desc}
        </Tag>
      );
    case OrderStatusMap.Progressing.type:
      return (
        <Tag color={'volcano'}>
          <LoadingOutlined /> {OrderStatusMap.Progressing.desc}
        </Tag>
      );
    case OrderStatusMap.Complete.type:
      return (
        <Tag color={'#87d068'}>
          <CheckCircleOutlined /> {OrderStatusMap.Complete.desc}
        </Tag>
      );
    case OrderStatusMap.SystemComplete.type:
      return (
        <Tag color={'#87d068'}>
          <CheckCircleOutlined /> {OrderStatusMap.SystemComplete.desc}
        </Tag>
      );
  }

  return null;
};

const iconStyle = {
  fontSize: '1.1em',
};

export const getPaymentChannelDesc = (code: PaymentChannel) => {
  let result: React.ReactNode;
  switch (code) {
    case PaymentChannel.Cash:
      result = (
        <>
          <PayCircleFilled style={{ ...iconStyle, color: 'goldenrod' }} /> 现金
        </>
      );
      break;
    case PaymentChannel.Alipay:
      result = (
        <>
          <AlipayCircleFilled style={{ ...iconStyle, color: '#2f77ff' }} /> 支付宝
        </>
      );
      break;
    case PaymentChannel.Tenpay:
      result = (
        <>
          <WechatFilled style={{ ...iconStyle, color: '#31ac38' }} /> 微信
        </>
      );
      break;
    case PaymentChannel.Groupon_MT:
      result = (
        <>
          <IconMT style={iconStyle} /> 美团团购
        </>
      );
      break;
    case PaymentChannel.Groupon_DY:
      result = (
        <>
          <IconDY style={iconStyle} /> 抖音团购
        </>
      );
      break;
    case PaymentChannel.INVALID:
      result = (
        <>
          <CheckCircleTwoTone style={iconStyle} /> 无需实付
        </>
      );
      break;
    default:
      result = 'N/A';
  }
  return (
    <Space direction="horizontal" size={5}>
      {result}
    </Space>
  );
};
